import { Extension } from "@tiptap/core";

export interface IndentLevelOptions {
  types: string[];
  maxLevel: number;
  step: number;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    indentLevel: {
      setIndentLevel: (level: number) => ReturnType;
      increaseIndent: () => ReturnType;
      decreaseIndent: () => ReturnType;
    };
  }
}

/**
 * Per-block indent level (0-maxLevel), same shape as the official TextAlign
 * extension (addGlobalAttributes + addCommands). Renders as
 * `style="margin-left:…"`; level 0 renders no attribute. Only meaningful for
 * plain paragraphs/headings — list-item nesting (sinkListItem/liftListItem)
 * is a separate mechanism wired in MenuBar/Toolbar, not this extension.
 */
export const IndentLevel = Extension.create<IndentLevelOptions>({
  name: "indentLevel",

  addOptions() {
    return { types: [], maxLevel: 8, step: 24 };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indentLevel: {
            default: 0,
            parseHTML: (element: HTMLElement) => {
              const px = parseFloat(element.style.marginLeft || "0");
              if (!px) return 0;
              return Math.max(0, Math.min(this.options.maxLevel, Math.round(px / this.options.step)));
            },
            renderHTML: (attributes: Record<string, any>) => {
              const level = attributes.indentLevel as number;
              if (!level) return {};
              const px = level * this.options.step;
              // Also exposed as --qda-indent so the paragraph-number gutter
              // (paragraphGutterPlugin.ts, positioned absolute against this
              // same block) can subtract it back out — margin-left shifts
              // this element's border box, which is the gutter number's
              // positioning context, so without this the number would drift
              // right with the indent instead of staying in a fixed column.
              return { style: `margin-left: ${px}px; --qda-indent: ${px}px` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setIndentLevel:
        (level: number) =>
        ({ commands }) => {
          const clamped = Math.max(0, Math.min(this.options.maxLevel, level));
          return this.options.types
            .map((type) => commands.updateAttributes(type, { indentLevel: clamped }))
            .every(Boolean);
        },
      increaseIndent:
        () =>
        ({ editor, commands }) => {
          const activeType = this.options.types.find((type) => editor.isActive(type));
          const current = activeType ? (editor.getAttributes(activeType).indentLevel as number) || 0 : 0;
          return commands.setIndentLevel(current + 1);
        },
      decreaseIndent:
        () =>
        ({ editor, commands }) => {
          const activeType = this.options.types.find((type) => editor.isActive(type));
          const current = activeType ? (editor.getAttributes(activeType).indentLevel as number) || 0 : 0;
          return commands.setIndentLevel(current - 1);
        },
    };
  },
});
